import { useState } from "react";
import { ShieldCheck, ShieldWarning, ArrowClockwise } from "@phosphor-icons/react";
import { act, api, useFetch, fmtDate } from "@/lib/api";
import { Panel } from "@/components/common";
import { Button } from "@/components/ui/button";

const STATE = {
  ok: ["Sending normally", "text-emerald-400", ShieldCheck],
  probing: ["Testing the mailbox", "text-amber-400", ShieldWarning],
  blocked: ["Sending paused", "text-red-400", ShieldWarning],
};

const Stat = ({ k, v, id }) => (
  <div data-testid={id}><div className="text-xs text-zinc-500">{k}</div><div className="text-lg font-semibold text-white">{v}</div></div>
);

const Box = ({ b, i }) => {
  const g = b.guard;
  const [label, tone, Icon] = STATE[g.state] || STATE.ok;
  return (
    <div className="border border-border rounded-md p-3" data-testid={`deliverability-box-${i}`}>
      <div className="flex items-center gap-2 text-sm">
        <span className="text-white font-medium">{b.mailbox}</span>
        <span className={`flex items-center gap-1 font-semibold ${tone}`} data-testid={i === 0 ? "deliverability-state" : `deliverability-state-${i}`}><Icon size={16} weight="bold" />{label}</span>
        <span className="ml-auto text-xs text-zinc-500">{b.sent_today} sent today · pace {g.cap}/day</span>
      </div>
      {g.state !== "ok" && <p className="text-xs text-zinc-400 mt-1" data-testid={i === 0 ? "deliverability-reason" : `deliverability-reason-${i}`}>{g.reason}{g.state === "blocked" && g.resume_at ? ` - next test ${fmtDate(g.resume_at)}` : ""}</p>}
    </div>
  );
};

export const DeliverabilityPanel = () => {
  const { data, reload } = useFetch("/deliverability");
  const [busy, setBusy] = useState(false);
  if (!data) return null;
  const boxes = data.mailboxes || [];
  const b = data.bounces || {};
  const blocked = boxes.some((x) => x.guard.state === "blocked" && x.guard.kind === "sender_block");
  const check = async () => {
    setBusy(true);
    await act(() => api.post("/deliverability/check"), "Mailbox check started - MaSa resumes on its own if the test gets through");
    setBusy(false);
    reload();
  };
  return (
    <Panel title="Email deliverability - AI managed" data-testid="deliverability-panel">
      <div className="flex flex-col md:flex-row md:items-start gap-6">
        <div className="flex-1 space-y-3">
          {boxes.map((x, i) => <Box key={x.mailbox} b={x} i={i} />)}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Stat id="deliv-pace" k="Pace today" v={`${boxes.reduce((n, x) => n + Number(x.guard.cap || 0), 0)}/day`} />
            <Stat id="deliv-sent-today" k="Sent today" v={data.sent_today} />
            <Stat id="deliv-waiting" k="Waiting to send" v={data.waiting} />
            <Stat id="deliv-hard" k="Bad addresses removed" v={b.recipient_invalid || 0} />
            <Stat id="deliv-blocked" k="Blocked by Microsoft" v={b.sender_blocked || 0} />
          </div>
          <ul className="text-xs text-zinc-500 space-y-1" data-testid="deliverability-events">
            {data.events.length === 0 && <li>No activity yet - MaSa logs every pause, test and pace change here.</li>}
            {data.events.map((e) => <li key={e.id}>{fmtDate(e.created_at)} - {e.text}</li>)}
          </ul>
        </div>
        {blocked && (
          <div className="md:w-72 space-y-2">
            <p className="text-xs text-zinc-400">Unblocked the mailbox at security.microsoft.com → Restricted entities? Let MaSa test it now.</p>
            <Button data-testid="deliverability-check-btn" disabled={busy} onClick={check} className="gap-2 w-full">
              <ArrowClockwise weight="bold" /> I've unblocked it - test now
            </Button>
          </div>
        )}
      </div>
    </Panel>
  );
};
